import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskTypeRequest, TaskTypeResponse } from '../../../models/task.model';
import { slugifyToCode } from '../../../utils/slugify-code.util';
import { ToastService } from '../../../../../shared/services/toast.service';

@Component({
  selector: 'app-task-types-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './task-types-form.component.html',
})
export class TaskTypesFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TaskTypeResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  /** Cuando true, no auto-completar name desde label (usuario lo editó manualmente). */
  private nameManuallyEdited = false;

  readonly COLOR_PRESETS = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#84CC16', '#22C55E', '#10B981', '#14B8A6',
    '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#F43F5E', '#6B7280',
  ];

  constructor(private fb: FormBuilder, private api: TaskCatalogApiService, private toast: ToastService) {
    this.initForm();
  }

  ngOnInit(): void {}

  // displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(100)]],
      color: ['#4CAF50', [Validators.maxLength(30)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.nameManuallyEdited = false;
    this.initForm();
  }

  loadItem(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.itemId = id;
    this.nameManuallyEdited = true; // edición — no auto-rellenar
    this.api.getTypeById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          label: item.label,
          color: item.color ?? '#4CAF50',
          isActive: item.isActive ?? true,
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el tipo';
        this.loading = false;
      },
    });
  }

  onLabelInput(value: string): void {
    if (!this.nameManuallyEdited) {
      this.form.patchValue({ name: slugifyToCode(value) }, { emitEvent: false });
    }
  }

  onNameInput(): void {
    // Marca que el usuario editó manualmente el código
    this.nameManuallyEdited = true;
  }

  setColor(c: string): void {
    this.form.patchValue({ color: c });
  }

  toggleActive(): void {
    this.form.patchValue({ isActive: !this.form.value.isActive });
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const body: TaskTypeRequest = {
      name: v.name.trim().toUpperCase(),
      label: v.label.trim(),
      color: v.color || undefined,
      isActive: v.isActive ?? true,
    };

    const req$ = this.isEditing && this.itemId
      ? this.api.updateType(this.itemId, body)
      : this.api.createType(body);

    req$.subscribe({
      next: (item) => { this.saving = false; this.saved.emit(item); },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el tipo de tarea';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  cancelar(): void { this.cancelled.emit(); }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
